import { FC, useEffect, useState } from 'react'
import Select from 'react-select'
import ReactDatePicker from 'react-datepicker'
import moment from 'moment'

import { MetricsCard } from './components/MetricsCard'
import { DashboardTable } from './components/MetricsTable'
import { ROLES } from '../../../Utils/Constant/roles'
import { useDispatch, useSelector } from 'react-redux'
import { getAllUserDetails } from '../../UserPages/accounts/Overview/OverviewSlice/UserDetailsSlice'
import { useLazyGetSalesPerformanceListQuery, useUpdateStatusUsedCouponMutation } from '../../../../services/cellcast'
import { GetAllStaff } from '../MyProfile/Staff_Account/StaffAccountSlice/StaffAccountSlice'
import { KTSVG } from '../../../../_metronic/helpers'
import { toast } from 'react-toastify'

const DashboardPerformance: FC = () => {
  const dispatch = useDispatch()
  const userDetail = useSelector(getAllUserDetails)
  const stafflist = useSelector((state: any) => state?.StaffAccount?.staffAccountList)
  const staffListOptions = () => {
    const defaultOptions = [{
      value: ``,
      label: `All`,
    }]

    const list = stafflist?.items ? stafflist?.items?.map((key: any) => ({
      value: `${key?._id}`,
      label: `${key?.name}`,
    })) : [];
    return [...defaultOptions, ...list];
  };

  const today = new Date()
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0)

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([startOfMonth, new Date()])
  const [startDate, endDate] = dateRange

  const [params, setParams] = useState({
    staffId: userDetail?.role === ROLES.SUPER_ADMIN ? '' : userDetail?._id,
    page: 1,
    limit: 10,
    from: moment(startDate).format('YYYY-MM-DD'),
    to: moment(endDate).format('YYYY-MM-DD'),
    coupon: ''
  })

  const [fetch, { isLoading, data }] = useLazyGetSalesPerformanceListQuery()
  const [updateStatusUsedCouponMutation] = useUpdateStatusUsedCouponMutation()

  useEffect(() => {
    fetch(params)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params])

  useEffect(() => {
    dispatch(
      GetAllStaff({
        page: {
          page: 1,
          size: 10,
          search: '',
        },
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSetParams = (field: string, value: string) => {
    setParams((params) => ({
      ...params,
      [field]: value,
    }));
  };

  const setDateParams = (dateRangeParams: [Date | null, Date | null]) => {
    let [startDate, endDate] = dateRangeParams

    if (endDate === null) {
      endDate = new Date()
    }

    setParams({
      ...params,
      from: moment(startDate).format('YYYY-MM-DD'),
      to: moment(endDate).format('YYYY-MM-DD'),
    })
    setDateRange(dateRangeParams)
  }

  const handleUpdateStatusUsedCoupon = async (id: string, status: boolean) => {
    try {
      const result = await updateStatusUsedCouponMutation({ userId: id, hasUsedCoupon: status }).unwrap()
      if (result) {
        toast.success('Status updated successfully')
      } else {
        toast.error('Failed to update status')
      }
      fetch(params)
    } catch (error) {
      console.error('Failed to update the status of the used coupon:', error)
       toast.error('Failed to update status')
    }
  }

  const allMetrics =
  {
    coupon: [
      {
        title: 'Coupon Applied',
        value: data?.data?.summary?.applied ?? 0,
        color: 'white',
        icon: '/media/icons/duotune/ecommerce/ecm008.svg'
      },
      {
        title: 'Unused Coupon',
        value: data?.data?.summary?.unused ?? 0,
        color: 'white',
        icon: '/media/icons/duotune/ecommerce/ecm010.svg'
      },
      {
        title: 'Expired Coupon',
        value: data?.data?.summary?.expired ?? 0,
        color: 'white',
        icon: '/media/icons/duotune/general/gen040.svg'
      },
      {
        title: 'Used Coupon',
        value: data?.data?.summary?.used ?? 0,
        color: 'white',
        icon: '/media/icons/duotune/ecommerce/ecm009.svg'
      },
      {
        title: 'Commission',
        value: `$${(data?.data?.summary?.commission ?? 0).toFixed(3)}`,
        color: 'white',
        icon: '/media/icons/duotune/finance/fin001.svg'
      },
    ],
    user: [
      {
        title: 'Review Sent',
        value: data?.data?.summary?.reviewSent ?? 0,
        color: 'white',
        icon: '/media/icons/duotune/communication/com012.svg'
      },
      {
        title: 'Review Received',
        value: data?.data?.summary?.reviewReceived ?? 0,
        color: 'white',
        icon: '/media/icons/duotune/communication/com013.svg'
      },
      {
        title: 'Account at Risk',
        value: data?.data?.summary?.accountAtRisk ?? 0,
        color: 'white',
        icon: '/media/icons/duotune/general/gen044.svg'
      },
      {
        title: 'Account Saved',
        value: data?.data?.summary?.accountSaved ?? 0,
        color: 'white',
        icon: '/media/icons/duotune/general/gen045.svg'
      },
      {
        title: 'Sav. Commission',
        value: `$${(data?.data?.summary?.savedCommission ?? 0).toFixed(3)}`,
        color: 'white',
        icon: '/media/icons/duotune/finance/fin002.svg'
      }
    ]
  }

  return (
    <div className='mt-8'>
      <div className='row g-5 g-xl-8'>
        <div className='col-xl-6'>
          <MetricsCard
            data={allMetrics.coupon}
          />
        </div>
        <div className='col-xl-6'>
          <MetricsCard
            data={allMetrics.user}
          />
        </div>
      </div>

      <div className='card mt-8'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3'>Accounts Overview</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>Latest account statistics</span>
          </h3>

          <div className='d-flex align-items-center gap-4'>
            {/* Search */}
            <div className='position-relative'>
              <KTSVG
                path='/media/icons/duotune/general/gen021.svg'
                className='svg-icon-1 position-absolute top-50 translate-middle-y ms-4'
              />
              <input
                type='text'
                value={params.coupon || ''}
                data-kt-user-table-filter='coupon'
                className='form-control form-control-solid w-200px ps-10'
                placeholder='Search by coupon code'
                onChange={(e: any) => setParams({ ...params, coupon: e.target.value, page: 1 })}
              />
            </div>

            {userDetail?.role === ROLES.SUPER_ADMIN && (
              <Select
                onChange={(e: any) => handleSetParams('staffId', e.value)}
                options={staffListOptions()}
                className='w-200px'
              />
            )}

            {/* Date range */}
            <div className='d-flex'>
              <ReactDatePicker
                selectsRange
                dateFormat='MMM d, yyyy'
                className='form-control form-control-solid border-0 rounded-0'
                placeholderText='Pick date & time'
                onChange={(update) => setDateParams(update)}
                startDate={startDate}
                endDate={endDate}
                disabled={isLoading}
              />
              <span className='input-group-text bg-light border-0 rounded-0'>
                <i className='bi bi-calendar'></i>
              </span>
            </div>
          </div>
        </div>
        <DashboardTable
          data={data?.data?.items ?? []}
          isLoading={isLoading}
          updateStatusUsedCoupon={handleUpdateStatusUsedCoupon}
        />
      </div>
    </div>
  )
}

export default DashboardPerformance